import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  LinearProgress,
  FormControlLabel,
  Checkbox,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Link,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  WarningAmber as WarningAmberIcon,
  Download as DownloadIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Email as EmailIcon,
  Upload as UploadIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as ContentCopyIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { googleApi } from "../../../../services/api/invoice/google";
import { sheetsSettingsApi } from "../../../../services/api/invoice/sheets";
import { gmailSettingsApi } from "../../../../services/api/invoice/gmail";
import { invoiceDownloadApi } from "../../../../services/api/invoice/download";
import { createInvoiceWebSocket } from "../../../../services/api/invoice/base";

const BUCHHALTUNG_REPORT_TYPE_BY_KEY = {
  "otto_umsatz.csv": "otto_umsatz",
  "plenty_umsatz.csv": "plenty_umsatz",
  "kontoauszuege.csv": "kontoauszuege",
  "shopify_payouts.csv": "shopify_payouts",
  "shopify_order_transactions.csv": "shopify_order_transactions",
};

const IONOS_WEBMAIL_URL = "https://email.ionos.de/";

const EMAIL_BODY_TOKENS = [
  { token: "{{datev_link}}", label: "Datev link" },
  { token: "{{inventur_date}}", label: "Inventur date" },
  { token: "{{inventur_net_eur}}", label: "Inventur net" },
];

const DATEV_LINK_PLACEHOLDER = "Hier hinterlegen wir den Link manuell";
const INVENTUR_NET_PLACEHOLDER = "XXX";
const INVENTUR_DATE_PLACEHOLDER = "XX.XX.XXXX";

const renderEmailBodyClient = (
  template,
  { datevLink = "", inventurDate = "", inventurNet = "" } = {},
) => {
  const link = String(datevLink || "").trim() || DATEV_LINK_PLACEHOLDER;
  const net = String(inventurNet || "").trim() || INVENTUR_NET_PLACEHOLDER;
  const date = String(inventurDate || "").trim() || INVENTUR_DATE_PLACEHOLDER;
  return String(template || "")
    .split("{{datev_link}}")
    .join(link)
    .split("{{inventur_date}}")
    .join(date)
    .split("{{inventur_net_eur}}")
    .join(net);
};

const formatFileSize = (bytes) => {
  if (bytes == null || Number.isNaN(Number(bytes))) return "";
  const size = Number(bytes);
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const bankCreditcardItemGroup = (item) =>
  item?.group ||
  (String(item?.key || "").startsWith("otto_") ? "otto" : "bank");

const groupBankCreditcardItems = (items = []) => {
  const bank = [];
  const otto = [];
  for (const item of items) {
    if (bankCreditcardItemGroup(item) === "otto") {
      otto.push(item);
    } else {
      bank.push(item);
    }
  }
  return [
    {
      id: "bank",
      title: "Bank & credit cards",
      items: bank,
    },
    {
      id: "otto",
      title: "Otto fee statements",
      items: otto,
    },
  ].filter((group) => group.items.length > 0);
};

const bankCreditcardItemSummary = (item, { missingLabel = "Missing" } = {}) => {
  if (!item?.found) {
    return item?.multi_file
      ? "No files yet — upload one or more"
      : missingLabel;
  }
  if (item.multi_file) {
    const names = (item.files || []).map((file) => file.name);
    const preview =
      names.length <= 3
        ? names.join(", ")
        : `${names.slice(0, 3).join(", ")} +${names.length - 3} more`;
    const size =
      item.size_bytes != null ? ` · ${formatFileSize(item.size_bytes)}` : "";
    return `${item.file_count || names.length} file${
      (item.file_count || names.length) === 1 ? "" : "s"
    }${size}${preview ? ` · ${preview}` : ""}`;
  }
  return `${item.filename || ""}${
    item.size_bytes != null ? ` · ${formatFileSize(item.size_bytes)}` : ""
  }`;
};

const DownloadSubTab = () => {
  const isSuperuser = Boolean(
    useSelector((state) => state.auth?.user?.is_superuser),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Download states
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadStatus, setDownloadStatus] = useState("idle"); // 'idle', 'running', 'completed', 'error'
  const [downloadStatusMessage, setDownloadStatusMessage] = useState("");
  const [downloadStats, setDownloadStats] = useState({
    totalEmails: 0,
    processedEmails: 0,
    successfulDownloads: 0,
    failedDownloads: 0,
    alreadyDownloaded: 0,
    currentStep: "",
  });
  const [websocket, setWebsocket] = useState(null);

  // Date selection states
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  // ZIP download states
  const [availableZips, setAvailableZips] = useState([]);
  const [selectedZipYear, setSelectedZipYear] = useState("");
  const [selectedZipMonth, setSelectedZipMonth] = useState("");
  const [zipLoading, setZipLoading] = useState(false);
  const [includeShoppingTable, setIncludeShoppingTable] = useState(true);
  const [includePlatforms, setIncludePlatforms] = useState(true);
  const [includeReports, setIncludeReports] = useState(true);
  const [sortPlatformsInFolders, setSortPlatformsInFolders] = useState(false);
  const [sortReportsInFolders, setSortReportsInFolders] = useState(false);
  const [batchShoppingTable, setBatchShoppingTable] = useState(true);
  const [shoppingTableBatchSize, setShoppingTableBatchSize] = useState(300);

  const [buchhaltungDialogOpen, setBuchhaltungDialogOpen] = useState(false);
  const [buchhaltungLoading, setBuchhaltungLoading] = useState(false);
  const [buchhaltungSending, setBuchhaltungSending] = useState(false);
  const [buchhaltungPreview, setBuchhaltungPreview] = useState(null);
  const [buchhaltungHumanVerified, setBuchhaltungHumanVerified] =
    useState(false);
  const [buchhaltungSendConfirmOpen, setBuchhaltungSendConfirmOpen] =
    useState(false);
  const [buchhaltungZipLoading, setBuchhaltungZipLoading] = useState(false);
  const [datevLink, setDatevLink] = useState("");
  const [inventurNetEur, setInventurNetEur] = useState("");
  const [emailBodyTemplate, setEmailBodyTemplate] = useState("");
  const [emailBodyDirty, setEmailBodyDirty] = useState(false);
  const [emailBodySaving, setEmailBodySaving] = useState(false);
  const emailBodyDirtyRef = useRef(false);
  const emailBodyInputRef = useRef(null);
  const [uploadingReportKey, setUploadingReportKey] = useState(null);
  const buchhaltungFileInputRef = useRef(null);
  const pendingReportTypeRef = useRef(null);
  const bankCreditcardFileInputRef = useRef(null);
  const pendingBankCreditcardKeyRef = useRef(null);
  const [uploadingBankCreditcardKey, setUploadingBankCreditcardKey] =
    useState(null);

  const [manualPlatformStatus, setManualPlatformStatus] = useState(null);
  const [manualPlatformLoading, setManualPlatformLoading] = useState(false);
  const [uploadingPlatform, setUploadingPlatform] = useState(null);
  const platformFileInputRef = useRef(null);
  const pendingPlatformRef = useRef(null);

  const [bankCreditcardStatus, setBankCreditcardStatus] = useState(null);
  const [bankCreditcardLoading, setBankCreditcardLoading] = useState(false);

  const [failedInvoiceStatus, setFailedInvoiceStatus] = useState(null);
  const [failedInvoiceLoading, setFailedInvoiceLoading] = useState(false);
  const [uploadingFailedInvoices, setUploadingFailedInvoices] = useState(false);
  const failedInvoiceFileInputRef = useRef(null);
  const failedInvoiceFolderInputRef = useRef(null);

  // System status state
  const [systemStatus, setSystemStatus] = useState({
    hasActiveCredential: false,
    hasValidSheetSettings: false,
    hasGmailSettings: false,
    gmailSettingsCount: 0,
    isReady: false,
  });

  // Cleanup WebSocket on component unmount
  useEffect(() => {
    return () => {
      if (websocket) {
        console.log("Cleaning up WebSocket on component unmount");
        websocket.close();
        setWebsocket(null);
      }
    };
  }, [websocket]);

  // Check download status on page load and initialize WebSocket if process is running
  useEffect(() => {
    const checkDownloadStatus = async () => {
      try {
        const response = await invoiceDownloadApi.getDownloadStatus();
        const status = response.data;

        if (status.is_running) {
          console.log("Download process is running, restoring state:", status);

          // Restore progress state
          setDownloadStatus(status.status);
          setDownloadProgress(status.progress);
          setDownloadStatusMessage(status.message);
          setDownloadStats(status.stats);

          // Initialize WebSocket to receive live updates
          if (!websocket) {
            console.log("Initializing WebSocket for active download process");
            initializeWebSocket();
          }
        }
      } catch (error) {
        console.error("Failed to check download status:", error);
        // Don't show error to user, just log it
      }
    };

    checkDownloadStatus();
  }, []);

  // API functions
  // Download Functions
  const loadSystemStatus = async () => {
    try {
      // Load all required data in parallel
      const [credentialsResponse, sheetsResponse, gmailResponse] =
        await Promise.allSettled([
          googleApi.getCredentials(),
          sheetsSettingsApi.getSettings(),
          gmailSettingsApi.getAllSettings(),
        ]);

      // Check credentials
      const credentials =
        credentialsResponse.status === "fulfilled"
          ? credentialsResponse.value.data
          : [];
      const hasActiveCredential = credentials.some(
        (cred) => cred.active && cred.status === "authorized",
      );

      // Check sheets settings
      const sheetsSettings =
        sheetsResponse.status === "fulfilled"
          ? sheetsResponse.value.data
          : null;
      const hasValidSheetSettings =
        sheetsSettings &&
        sheetsSettings.spreadsheet_id &&
        (sheetsSettings.date_column ||
          sheetsSettings.order_place_column ||
          sheetsSettings.invoice_number_column ||
          sheetsSettings.confirmation_column);

      // Check Gmail settings
      const gmailSettings =
        gmailResponse.status === "fulfilled" ? gmailResponse.value.data : [];
      const hasGmailSettings = gmailSettings.length > 0;

      const isReady =
        hasActiveCredential && hasValidSheetSettings && hasGmailSettings;

      setSystemStatus({
        hasActiveCredential,
        hasValidSheetSettings,
        hasGmailSettings,
        gmailSettingsCount: gmailSettings.length,
        isReady,
      });
    } catch (error) {
      console.error("Failed to load system status:", error);
      setSystemStatus({
        hasActiveCredential: false,
        hasValidSheetSettings: false,
        hasGmailSettings: false,
        gmailSettingsCount: 0,
        isReady: false,
      });
    }
  };

  useEffect(() => {
    loadSystemStatus();
  }, []);

  const initializeWebSocket = () => {
    const handleMessage = (data) => {
      console.log("Handling WebSocket message:", data);
      switch (data.type) {
        case "progress":
          console.log("Processing progress update:", data.progress);
          // Apply progress updates directly - no throttling
          setDownloadProgress(data.progress);
          setDownloadStats(data.stats);
          setDownloadStatusMessage(data.message);

          // Reset status to idle when progress reaches 100%
          if (data.progress >= 100) {
            setDownloadStatus("idle");
          }
          break;
        case "status":
          console.log("Processing status update:", data.status);
          setDownloadStatus(data.status);
          setDownloadStatusMessage(data.message);
          break;
        case "completed":
          console.log("Processing completion message");
          setDownloadStatus("idle"); // Reset to idle so the button shows "Start Download" again
          setDownloadProgress(100);
          setDownloadStatusMessage(data.message);
          setSuccess("Download completed successfully!");
          break;
        case "error":
          console.log("Processing error message:", data.message);
          setDownloadStatus("error");
          setDownloadStatusMessage(data.message);
          setError(`Download failed: ${data.message}`);
          break;
        default:
          console.log("Unknown message type:", data.type, data);
      }
    };

    const handleError = (error) => {
      console.error("WebSocket error:", error);
      setError("WebSocket connection failed");
    };

    const ws = createInvoiceWebSocket(handleMessage, handleError);

    ws.onopen = () => {
      console.log("WebSocket connected");
      setWebsocket(ws);
    };

    ws.onclose = () => {
      console.log("WebSocket disconnected");
      setWebsocket(null);
    };

    return ws;
  };

  const startDownload = async () => {
    try {
      setDownloadStatus("running");
      setDownloadProgress(0);
      setDownloadStatusMessage("Initializing download...");
      setError(null);

      // Close existing WebSocket connection if any
      if (websocket) {
        console.log("Closing existing WebSocket connection");
        websocket.close();
        setWebsocket(null);
      }

      // Initialize WebSocket connection
      const ws = initializeWebSocket();

      // Start download process via API
      const response = await invoiceDownloadApi.startDownload(
        selectedYear,
        selectedMonth,
      );

      setDownloadStatusMessage(
        response.data.message ||
          `Download started for ${new Date(
            selectedYear,
            selectedMonth - 1,
          ).toLocaleString("default", { month: "long", year: "numeric" })}`,
      );
    } catch (error) {
      console.error("Failed to start download:", error);
      setDownloadStatus("error");
      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to start download",
      );
    }
  };

  const stopDownload = async () => {
    try {
      if (websocket) {
        websocket.close();
      }

      await invoiceDownloadApi.stopDownload();

      setDownloadStatus("idle");
      setDownloadProgress(0);
      setDownloadStatusMessage("Download stopped");
    } catch (error) {
      console.error("Failed to stop download:", error);
      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to stop download",
      );
    }
  };

  // ZIP download functions
  const fetchAvailableZips = async () => {
    setZipLoading(true);
    try {
      const response = await invoiceDownloadApi.getAvailableZips();
      setAvailableZips(response.data.available_zips || []);
      // Clear selections if they're no longer valid
      const zips = response.data.available_zips || [];
      if (selectedZipYear && selectedZipMonth) {
        const isValid = zips.some(
          (zip) =>
            zip.year === parseInt(selectedZipYear) &&
            zip.month === parseInt(selectedZipMonth),
        );
        if (!isValid) {
          setSelectedZipYear("");
          setSelectedZipMonth("");
        }
      }
    } catch (error) {
      console.error("Failed to fetch available ZIPs:", error);
      setError("Failed to load available ZIP files");
      setAvailableZips([]);
    } finally {
      setZipLoading(false);
    }
  };

  const downloadZip = async () => {
    if (!selectedZipYear || !selectedZipMonth) {
      setError("Please select a year and month for ZIP download");
      return;
    }

    if (!includeShoppingTable && !includePlatforms && !includeReports) {
      setError(
        "Select at least Shopping-Table invoices, Platform invoices, or Reports",
      );
      return;
    }

    const batchSize = Number(shoppingTableBatchSize);
    if (
      batchShoppingTable &&
      (!Number.isInteger(batchSize) || batchSize < 1 || batchSize > 10000)
    ) {
      setError(
        "Shopping-Table batch size must be an integer between 1 and 10000",
      );
      return;
    }

    setZipLoading(true);
    try {
      const response = await invoiceDownloadApi.downloadZip(
        selectedZipYear,
        selectedZipMonth,
        {
          includeShoppingTable,
          includePlatforms,
          includeReports,
          sortPlatformsInFolders,
          sortReportsInFolders,
          batchShoppingTable,
          shoppingTableBatchSize: batchSize || 300,
        },
      );

      // Create download URL from blob
      const url = window.URL.createObjectURL(response.data);

      // Create temporary link and trigger download
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoices_${selectedZipYear}_${selectedZipMonth
        .toString()
        .padStart(2, "0")}.zip`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess("ZIP file downloaded successfully!");
    } catch (error) {
      console.error("Failed to download ZIP:", error);
      setError(error.response?.data?.detail || "Failed to download ZIP file");
    } finally {
      setZipLoading(false);
    }
  };

  const getSelectedZipInfo = () => {
    if (!selectedZipYear || !selectedZipMonth) return null;
    return availableZips.find(
      (zip) =>
        zip.year === parseInt(selectedZipYear) &&
        zip.month === parseInt(selectedZipMonth),
    );
  };

  const formatZipMonthLabel = (zip) => {
    const monthLabel = new Date(2024, zip.month - 1).toLocaleString("default", {
      month: "long",
    });
    const shoppingCount = zip.shopping_table?.file_count ?? 0;
    const platformCount = Object.values(zip.platforms || {}).reduce(
      (sum, platform) => sum + (platform.file_count || 0),
      0,
    );
    return `${monthLabel} (${shoppingCount} shopping, ${platformCount} platform)`;
  };

  const getSelectedZipDownloadCount = () => {
    const info = getSelectedZipInfo();
    if (!info) return 0;

    let count = 0;
    if (includeShoppingTable) {
      count += info.shopping_table?.file_count ?? 0;
    }
    if (includePlatforms && info.platforms) {
      count += Object.values(info.platforms).reduce(
        (sum, platform) => sum + (platform.file_count || 0),
        0,
      );
    }
    return count;
  };

  const zipYearOptions = React.useMemo(() => {
    const years = new Set(availableZips.map((zip) => zip.year));
    const currentYear = new Date().getFullYear();
    years.add(currentYear);
    years.add(currentYear - 1);
    return [...years].sort((a, b) => b - a);
  }, [availableZips]);

  const loadManualPlatformStatus = async (
    year = selectedZipYear,
    month = selectedZipMonth,
  ) => {
    if (!year || !month) {
      setManualPlatformStatus(null);
      return null;
    }
    setManualPlatformLoading(true);
    try {
      const response = await invoiceDownloadApi.getManualPlatformInvoices(
        Number(year),
        Number(month),
      );
      setManualPlatformStatus(response.data);
      return response.data;
    } catch (err) {
      console.error("Failed to load manual platform invoice status:", err);
      setManualPlatformStatus(null);
      return null;
    } finally {
      setManualPlatformLoading(false);
    }
  };

  const loadBankCreditcardStatus = async (
    year = selectedZipYear,
    month = selectedZipMonth,
  ) => {
    if (!year || !month) {
      setBankCreditcardStatus(null);
      return null;
    }
    setBankCreditcardLoading(true);
    try {
      const response = await invoiceDownloadApi.getBankCreditcardReports(
        Number(year),
        Number(month),
      );
      setBankCreditcardStatus(response.data);
      return response.data;
    } catch (err) {
      console.error("Failed to load bank/credit-card status:", err);
      setBankCreditcardStatus(null);
      return null;
    } finally {
      setBankCreditcardLoading(false);
    }
  };

  const loadFailedInvoiceStatus = async (
    year = selectedZipYear,
    month = selectedZipMonth,
  ) => {
    if (!year || !month) {
      setFailedInvoiceStatus(null);
      return null;
    }
    setFailedInvoiceLoading(true);
    try {
      const response = await invoiceDownloadApi.getFailedInvoices(
        Number(year),
        Number(month),
      );
      setFailedInvoiceStatus(response.data);
      return response.data;
    } catch (err) {
      console.error("Failed to load failed-invoice upload status:", err);
      setFailedInvoiceStatus(null);
      return null;
    } finally {
      setFailedInvoiceLoading(false);
    }
  };

  const handleFailedInvoiceFilesSelected = async (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";

    if (!selected.length || !selectedZipYear || !selectedZipMonth) {
      return;
    }

    setUploadingFailedInvoices(true);
    try {
      setError(null);
      const response = await invoiceDownloadApi.uploadFailedInvoices(
        Number(selectedZipYear),
        Number(selectedZipMonth),
        selected,
      );
      const uploaded = response.data?.uploaded || [];
      const skipped = response.data?.skipped_existing || [];
      const parts = [];
      if (uploaded.length) {
        parts.push(`${uploaded.length} uploaded`);
      }
      if (skipped.length) {
        parts.push(`${skipped.length} already present`);
      }
      setSuccess(
        parts.length
          ? `Failed invoices: ${parts.join(", ")}.`
          : "No new failed invoices uploaded.",
      );
      await Promise.all([loadFailedInvoiceStatus(), fetchAvailableZips()]);
    } catch (err) {
      console.error("Failed to upload failed invoices:", err);
      setError(
        err.response?.data?.detail ||
          "Failed to upload invoices. Please try again.",
      );
    } finally {
      setUploadingFailedInvoices(false);
    }
  };

  const handleManualPlatformUploadClick = (platformItem) => {
    pendingPlatformRef.current = platformItem.platform;
    setUploadingPlatform(platformItem.platform);
    const input = platformFileInputRef.current;
    if (input) {
      input.multiple = Boolean(platformItem.allow_multiple);
      input.click();
    }
  };

  const handleManualPlatformFileSelected = async (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    const platform = pendingPlatformRef.current;
    pendingPlatformRef.current = null;

    if (
      !selected.length ||
      !platform ||
      !selectedZipYear ||
      !selectedZipMonth
    ) {
      setUploadingPlatform(null);
      return;
    }

    try {
      setError(null);
      const uploadedNames = [];
      for (const file of selected) {
        await invoiceDownloadApi.uploadManualPlatformInvoice(
          Number(selectedZipYear),
          Number(selectedZipMonth),
          platform,
          file,
        );
        uploadedNames.push(file.name);
      }
      setSuccess(
        uploadedNames.length === 1
          ? `${uploadedNames[0]} saved to invoices/${platform}/.`
          : `${uploadedNames.length} files saved to invoices/${platform}/.`,
      );
      const refreshTasks = [loadManualPlatformStatus(), fetchAvailableZips()];
      if (buchhaltungDialogOpen) {
        refreshTasks.push(loadBuchhaltungPreview());
      }
      await Promise.all(refreshTasks);
    } catch (err) {
      console.error("Failed to upload platform invoice:", err);
      setError(
        err.response?.data?.detail ||
          "Failed to upload platform invoice. Please try again.",
      );
    } finally {
      setUploadingPlatform(null);
    }
  };

  const loadBuchhaltungPreview = async (
    year = selectedZipYear,
    month = selectedZipMonth,
    options = {},
  ) => {
    if (!year || !month) return null;

    const nextDatevLink = options.datevLink ?? datevLink;
    const nextInventurNetEur = options.inventurNetEur ?? inventurNetEur;
    const preserveDirtyBody = options.preserveDirtyBody !== false;

    setBuchhaltungLoading(true);
    try {
      const response = await invoiceDownloadApi.getBuchhaltungPreview(
        year,
        month,
        {
          datevLink: nextDatevLink,
          inventurNetEur: nextInventurNetEur,
        },
      );
      setBuchhaltungPreview(response.data);
      if (response.data?.bank_creditcard_reports) {
        setBankCreditcardStatus(response.data.bank_creditcard_reports);
      }
      if (!nextInventurNetEur && response.data.inventur_net_eur) {
        setInventurNetEur(response.data.inventur_net_eur);
      }
      if (!(preserveDirtyBody && emailBodyDirtyRef.current)) {
        setEmailBodyTemplate(
          response.data?.body_template || response.data?.body || "",
        );
        setEmailBodyDirty(false);
        emailBodyDirtyRef.current = false;
      }
      return response.data;
    } catch (err) {
      console.error("Failed to load Buchhaltung preview:", err);
      setError(
        err.response?.data?.detail ||
          "Failed to load Buchhaltung preview. Please try again.",
      );
      return null;
    } finally {
      setBuchhaltungLoading(false);
    }
  };

  const openBuchhaltungDialog = async () => {
    if (!selectedZipYear || !selectedZipMonth) {
      setError("Please select a month for the accounting email.");
      return;
    }

    if (!isSuperuser && !manualPlatformStatus?.ready) {
      setError(
        "Please upload all manual platform invoices before sending the accounting email.",
      );
      return;
    }

    if (!isSuperuser && !bankCreditcardStatus?.ready) {
      setError(
        "Please upload all bank, credit card, and Otto fee statements before sending the accounting email.",
      );
      return;
    }

    setError(null);
    setSuccess(null);
    setBuchhaltungDialogOpen(true);
    await loadBuchhaltungPreview();
  };

  const manualPlatformInvoicesReady = manualPlatformStatus?.ready === true;
  const bankCreditcardReportsReady = bankCreditcardStatus?.ready === true;
  const accountingPrerequisitesReady =
    manualPlatformInvoicesReady && bankCreditcardReportsReady;
  const canOpenBuchhaltungReview =
    accountingPrerequisitesReady || isSuperuser;
  const buchhaltungSendReady = buchhaltungPreview?.ready === true;

  const renderedEmailBody = useMemo(
    () =>
      renderEmailBodyClient(emailBodyTemplate, {
        datevLink,
        inventurDate: buchhaltungPreview?.inventur_date || "",
        inventurNet: inventurNetEur || buchhaltungPreview?.inventur_net_eur || "",
      }),
    [
      emailBodyTemplate,
      datevLink,
      inventurNetEur,
      buchhaltungPreview?.inventur_date,
      buchhaltungPreview?.inventur_net_eur,
    ],
  );

  const closeBuchhaltungDialog = () => {
    setBuchhaltungDialogOpen(false);
    setBuchhaltungPreview(null);
    setBuchhaltungHumanVerified(false);
    setBuchhaltungSendConfirmOpen(false);
    setEmailBodyTemplate("");
    setEmailBodyDirty(false);
    emailBodyDirtyRef.current = false;
  };

  const refreshBuchhaltungPreview = async () => {
    await loadBuchhaltungPreview(undefined, undefined, {
      preserveDirtyBody: true,
    });
  };

  const insertEmailBodyToken = (token) => {
    const input = emailBodyInputRef.current;
    if (input && typeof input.selectionStart === "number") {
      const start = input.selectionStart;
      const end = input.selectionEnd;
      const next =
        emailBodyTemplate.slice(0, start) +
        token +
        emailBodyTemplate.slice(end);
      setEmailBodyTemplate(next);
      setEmailBodyDirty(true);
      emailBodyDirtyRef.current = true;
      requestAnimationFrame(() => {
        input.focus();
        const pos = start + token.length;
        input.setSelectionRange(pos, pos);
      });
      return;
    }
    setEmailBodyTemplate((prev) => `${prev}${token}`);
    setEmailBodyDirty(true);
    emailBodyDirtyRef.current = true;
  };

  const saveBuchhaltungEmailBody = async () => {
    if (!selectedZipYear || !selectedZipMonth || !emailBodyTemplate.trim()) {
      setError("Email body cannot be empty.");
      return false;
    }
    setEmailBodySaving(true);
    setError(null);
    try {
      const response = await invoiceDownloadApi.saveBuchhaltungEmailBody(
        Number(selectedZipYear),
        Number(selectedZipMonth),
        emailBodyTemplate,
      );
      const nextTemplate =
        response.data?.body_template || emailBodyTemplate;
      setEmailBodyTemplate(nextTemplate);
      setEmailBodyDirty(false);
      emailBodyDirtyRef.current = false;
      setBuchhaltungPreview((prev) =>
        prev
          ? {
              ...prev,
              body_template: nextTemplate,
              body_is_custom: true,
            }
          : prev,
      );
      setSuccess(
        "Email template saved for this month (shared for all users).",
      );
      return true;
    } catch (err) {
      console.error("Failed to save Buchhaltung email body:", err);
      setError(
        err.response?.data?.detail ||
          "Failed to save email body. Please try again.",
      );
      return false;
    } finally {
      setEmailBodySaving(false);
    }
  };

  const resetBuchhaltungEmailBody = async () => {
    if (!selectedZipYear || !selectedZipMonth) return;
    setEmailBodySaving(true);
    setError(null);
    try {
      await invoiceDownloadApi.clearBuchhaltungEmailBody(
        Number(selectedZipYear),
        Number(selectedZipMonth),
      );
      setEmailBodyDirty(false);
      emailBodyDirtyRef.current = false;
      await loadBuchhaltungPreview(undefined, undefined, {
        preserveDirtyBody: false,
      });
      setSuccess("Email template reset to the default (with placeholders).");
    } catch (err) {
      console.error("Failed to reset Buchhaltung email body:", err);
      setError(
        err.response?.data?.detail ||
          "Failed to reset email body. Please try again.",
      );
    } finally {
      setEmailBodySaving(false);
    }
  };

  const handleBuchhaltungUploadClick = (attachment) => {
    const reportType = BUCHHALTUNG_REPORT_TYPE_BY_KEY[attachment.key];
    if (!reportType) return;
    pendingReportTypeRef.current = reportType;
    setUploadingReportKey(attachment.key);
    buchhaltungFileInputRef.current?.click();
  };

  const handleBuchhaltungFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    const reportType = pendingReportTypeRef.current;
    pendingReportTypeRef.current = null;

    if (!file || !reportType || !selectedZipYear || !selectedZipMonth) {
      setUploadingReportKey(null);
      return;
    }

    try {
      setError(null);
      await invoiceDownloadApi.uploadBuchhaltungReport(
        Number(selectedZipYear),
        Number(selectedZipMonth),
        reportType,
        file,
      );
      setSuccess(`${file.name} saved to reports/.`);
      await loadBuchhaltungPreview();
    } catch (err) {
      console.error("Failed to upload Buchhaltung report:", err);
      setError(
        err.response?.data?.detail ||
          "Failed to upload report. Please try again.",
      );
    } finally {
      setUploadingReportKey(null);
    }
  };

  const handleBankCreditcardUploadClick = (item) => {
    pendingBankCreditcardKeyRef.current = item.key;
    setUploadingBankCreditcardKey(item.key);
    const input = bankCreditcardFileInputRef.current;
    if (input) {
      input.multiple = Boolean(item.multi_file);
      input.click();
    }
  };

  const handleBankCreditcardFileSelected = async (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    const reportKey = pendingBankCreditcardKeyRef.current;
    pendingBankCreditcardKeyRef.current = null;

    if (
      !selected.length ||
      !reportKey ||
      !selectedZipYear ||
      !selectedZipMonth
    ) {
      setUploadingBankCreditcardKey(null);
      return;
    }

    try {
      setError(null);
      const response = await invoiceDownloadApi.uploadBankCreditcardReport(
        Number(selectedZipYear),
        Number(selectedZipMonth),
        reportKey,
        selected,
      );
      const uploaded = response.data?.uploaded || [];
      const skipped = response.data?.skipped_existing || [];
      const parts = [];
      if (uploaded.length) {
        parts.push(`${uploaded.length} uploaded`);
      }
      if (skipped.length) {
        parts.push(`${skipped.length} already present`);
      }
      setSuccess(
        parts.length
          ? `${reportKey}: ${parts.join(
              ", ",
            )} → reports/.../bank_creditcard/ (not emailed).`
          : `${selected[0].name} saved to reports/.../bank_creditcard/ (not emailed).`,
      );
      const refreshTasks = [loadBankCreditcardStatus()];
      if (buchhaltungDialogOpen) {
        refreshTasks.push(loadBuchhaltungPreview());
      }
      await Promise.all(refreshTasks);
    } catch (err) {
      console.error("Failed to upload bank/credit-card report:", err);
      setError(
        err.response?.data?.detail ||
          "Failed to upload bank/credit-card report. Please try again.",
      );
    } finally {
      setUploadingBankCreditcardKey(null);
    }
  };

  const copyText = async (label, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setSuccess(`${label} copied to clipboard.`);
    } catch (err) {
      console.error("Failed to copy text:", err);
      setError(`Failed to copy ${label.toLowerCase()}.`);
    }
  };

  const downloadBuchhaltungAttachments = async () => {
    if (!selectedZipYear || !selectedZipMonth) return;
    setBuchhaltungZipLoading(true);
    setError(null);
    try {
      const response =
        await invoiceDownloadApi.downloadBuchhaltungAttachmentsZip(
          Number(selectedZipYear),
          Number(selectedZipMonth),
        );
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `buchhaltung_attachments_${selectedZipYear}_${String(
        selectedZipMonth,
      ).padStart(2, "0")}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSuccess(
        "Attachment ZIP downloaded. Upload it in Ionos after reviewing.",
      );
    } catch (err) {
      console.error("Failed to download Buchhaltung attachments:", err);
      setError(
        err.response?.data?.detail ||
          "Failed to download Buchhaltung attachments.",
      );
    } finally {
      setBuchhaltungZipLoading(false);
    }
  };

  const openIonosWebmail = () => {
    const url = buchhaltungPreview?.ionos_webmail_url || IONOS_WEBMAIL_URL;
    window.open(url, "_blank", "noopener,noreferrer");
    setSuccess(
      "Ionos Webmail opened. Paste the subject and body, attach the downloaded files, then send manually.",
    );
  };

  const sendBuchhaltung = async () => {
    if (!selectedZipYear || !selectedZipMonth || !buchhaltungHumanVerified)
      return;

    setBuchhaltungSending(true);
    setError(null);
    setSuccess(null);

    try {
      if (emailBodyDirty) {
        const saved = await saveBuchhaltungEmailBody();
        if (!saved) return;
      }
      await invoiceDownloadApi.sendBuchhaltung({
        year: Number(selectedZipYear),
        month: Number(selectedZipMonth),
        datev_link: datevLink,
        inventur_net_eur: inventurNetEur || null,
        body: emailBodyTemplate,
        confirmed: true,
      });
      setSuccess(
        "Accounting email was sent via the configured email server (n8n/SMTP).",
      );
      setBuchhaltungSendConfirmOpen(false);
      closeBuchhaltungDialog();
    } catch (err) {
      console.error("Failed to send Buchhaltung:", err);
      setError(
        err.response?.data?.detail ||
          "Failed to send Buchhaltung. Please try again.",
      );
    } finally {
      setBuchhaltungSending(false);
    }
  };

  useEffect(() => {
    fetchAvailableZips();
  }, []);

  useEffect(() => {
    if (selectedZipYear && selectedZipMonth) {
      loadManualPlatformStatus(selectedZipYear, selectedZipMonth);
      loadBankCreditcardStatus(selectedZipYear, selectedZipMonth);
      loadFailedInvoiceStatus(selectedZipYear, selectedZipMonth);
    } else {
      setManualPlatformStatus(null);
      setBankCreditcardStatus(null);
      setFailedInvoiceStatus(null);
    }
  }, [selectedZipYear, selectedZipMonth]);

  // Cleanup WebSocket on component unmount
  useEffect(() => {
    return () => {
      if (websocket) {
        websocket.close();
      }
    };
  }, [websocket]);

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h4" fontWeight="bold">
            Download Center
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {/* System Status and Date Selection */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* System Status Summary */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System Status
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    {systemStatus.hasActiveCredential ? (
                      <CheckCircleIcon sx={{ color: "success.main" }} />
                    ) : (
                      <ErrorIcon sx={{ color: "error.main" }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Google Credentials"
                    secondary={
                      systemStatus.hasActiveCredential
                        ? "Active and authorized credential found"
                        : "No active or authorized credentials"
                    }
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    {systemStatus.hasValidSheetSettings ? (
                      <CheckCircleIcon sx={{ color: "success.main" }} />
                    ) : (
                      <ErrorIcon sx={{ color: "error.main" }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Sheets Settings"
                    secondary={
                      systemStatus.hasValidSheetSettings
                        ? "Valid sheet configuration found"
                        : "Sheet settings incomplete"
                    }
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    {systemStatus.hasGmailSettings ? (
                      <CheckCircleIcon sx={{ color: "success.main" }} />
                    ) : (
                      <ErrorIcon sx={{ color: "error.main" }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Gmail Mappings"
                    secondary={
                      systemStatus.hasGmailSettings
                        ? `${systemStatus.gmailSettingsCount} Gmail mapping(s) configured`
                        : "No Gmail mappings configured"
                    }
                  />
                </ListItem>
              </List>

              {systemStatus.isReady ? (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    ✅ System is ready for download processing!
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    ⚠️ Please complete the configuration in other tabs before
                    starting downloads.
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Date Selection */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Download Period
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Year</InputLabel>
                  <Select
                    value={selectedYear}
                    label="Year"
                    onChange={(e) => setSelectedYear(e.target.value)}
                  >
                    {Array.from(
                      { length: 10 },
                      (_, i) => new Date().getFullYear() - i,
                    ).map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Month</InputLabel>
                  <Select
                    value={selectedMonth}
                    label="Month"
                    onChange={(e) => setSelectedMonth(e.target.value)}
                  >
                    {Array.from({ length: 12 }, (_, i) => ({
                      value: i + 1,
                      label: new Date(2024, i).toLocaleString("default", {
                        month: "long",
                      }),
                    })).map((month) => (
                      <MenuItem key={month.value} value={month.value}>
                        {month.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    📅 Selected:{" "}
                    {new Date(selectedYear, selectedMonth - 1).toLocaleString(
                      "default",
                      {
                        month: "long",
                        year: "numeric",
                      },
                    )}
                  </Typography>
                </Alert>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* ZIP Download Section */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Download ZIP Archive
              </Typography>

              {availableZips.length === 0 && !zipLoading ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    No invoice archives yet. Select year/month below to upload
                    manual platform invoices, or create shopping-table invoices
                    first.
                  </Typography>
                </Alert>
              ) : null}

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <FormControl fullWidth disabled={zipLoading}>
                  <InputLabel>Year</InputLabel>
                  <Select
                    value={selectedZipYear}
                    label="Year"
                    onChange={(e) => {
                      setSelectedZipYear(e.target.value);
                      setSelectedZipMonth("");
                    }}
                  >
                    {zipYearOptions.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl
                  fullWidth
                  disabled={!selectedZipYear || zipLoading}
                >
                  <InputLabel>Month</InputLabel>
                  <Select
                    value={selectedZipMonth}
                    label="Month"
                    onChange={(e) => setSelectedZipMonth(e.target.value)}
                  >
                    {Array.from({ length: 12 }, (_, index) => index + 1).map(
                      (month) => {
                        const zipInfo = availableZips.find(
                          (zip) =>
                            zip.year === parseInt(selectedZipYear) &&
                            zip.month === month,
                        );
                        const monthLabel = new Date(
                          2024,
                          month - 1,
                        ).toLocaleString("default", { month: "long" });
                        return (
                          <MenuItem key={month} value={month}>
                            {monthLabel}
                            {zipInfo
                              ? ` (${
                                  zipInfo.total_file_count ??
                                  zipInfo.file_count ??
                                  0
                                } files)`
                              : ""}
                          </MenuItem>
                        );
                      },
                    )}
                  </Select>
                </FormControl>

                <Divider />

                <Typography variant="subtitle2" color="text.secondary">
                  Include in ZIP
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeShoppingTable}
                      onChange={(e) =>
                        setIncludeShoppingTable(e.target.checked)
                      }
                    />
                  }
                  label="Shopping-Table invoices"
                />
                <FormControlLabel
                  sx={{ ml: 3 }}
                  control={
                    <Checkbox
                      checked={batchShoppingTable}
                      onChange={(e) => setBatchShoppingTable(e.target.checked)}
                      disabled={!includeShoppingTable}
                    />
                  }
                  label="Batch Shopping-Table into numbered folders"
                />
                <TextField
                  size="small"
                  type="number"
                  label="Batch size"
                  value={shoppingTableBatchSize}
                  onChange={(e) => setShoppingTableBatchSize(e.target.value)}
                  disabled={!includeShoppingTable || !batchShoppingTable}
                  inputProps={{ min: 1, max: 10000, step: 1 }}
                  helperText={
                    batchShoppingTable
                      ? `Invoices/Shopping-Table/1 … N (${
                          shoppingTableBatchSize || 300
                        } files each)`
                      : "Off: flat Invoices/Shopping-Table/"
                  }
                  sx={{ ml: 3, maxWidth: 220 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includePlatforms}
                      onChange={(e) => setIncludePlatforms(e.target.checked)}
                    />
                  }
                  label="Platform invoices (Hetzner, Sellerlogic, …)"
                />
                <FormControlLabel
                  sx={{ ml: 3 }}
                  control={
                    <Checkbox
                      checked={sortPlatformsInFolders}
                      onChange={(e) =>
                        setSortPlatformsInFolders(e.target.checked)
                      }
                      disabled={!includePlatforms}
                    />
                  }
                  label="Sort platform invoices in folders"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeReports}
                      onChange={(e) => setIncludeReports(e.target.checked)}
                    />
                  }
                  label="Month reports (reports/year/month → Reports/)"
                />
                <FormControlLabel
                  sx={{ ml: 3 }}
                  control={
                    <Checkbox
                      checked={sortReportsInFolders}
                      onChange={(e) =>
                        setSortReportsInFolders(e.target.checked)
                      }
                      disabled={!includeReports}
                    />
                  }
                  label="Sort reports in folders"
                />
                <Typography variant="caption" color="text.secondary">
                  ZIP layout:{" "}
                  {includeReports
                    ? sortReportsInFolders
                      ? "Reports/<subdir>/ + "
                      : "Reports/ (flat) + "
                    : ""}
                  Invoices/Shopping-Table
                  {batchShoppingTable ? "/1…N" : ""}
                  {includePlatforms
                    ? sortPlatformsInFolders
                      ? " + Invoices/Platforms/<vendor>/"
                      : " + Invoices/Platforms/ (flat)"
                    : ""}
                </Typography>

                {getSelectedZipInfo() && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      📦 {getSelectedZipDownloadCount()} invoice file(s)
                      selected
                      {includeReports ? " (+ month reports if present)" : ""}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Shopping-Table:{" "}
                      {getSelectedZipInfo().shopping_table?.file_count ?? 0}
                      {batchShoppingTable && includeShoppingTable
                        ? ` → ~${Math.max(
                            1,
                            Math.ceil(
                              (getSelectedZipInfo().shopping_table
                                ?.file_count ?? 0) /
                                Math.max(
                                  1,
                                  Number(shoppingTableBatchSize) || 300,
                                ),
                            ),
                          )} batches`
                        : ""}{" "}
                      · Platforms:{" "}
                      {Object.entries(getSelectedZipInfo().platforms || {})
                        .map(
                          ([vendor, data]) => `${vendor} (${data.file_count})`,
                        )
                        .join(", ") || "none"}
                      {includeReports ? " · Reports: included" : ""}
                    </Typography>
                    <Typography variant="caption" display="block">
                      File: {getSelectedZipInfo().zip_filename}
                    </Typography>
                  </Alert>
                )}

                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={
                    zipLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  onClick={downloadZip}
                  disabled={
                    !selectedZipYear ||
                    !selectedZipMonth ||
                    zipLoading ||
                    (!includeShoppingTable &&
                      !includePlatforms &&
                      !includeReports) ||
                    (getSelectedZipDownloadCount() === 0 && !includeReports)
                  }
                  sx={{ mt: 1 }}
                >
                  {zipLoading ? "Downloading..." : "Download ZIP"}
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={fetchAvailableZips}
                  disabled={zipLoading}
                  sx={{ mt: 1 }}
                  startIcon={zipLoading ? <CircularProgress size={16} /> : null}
                >
                  {zipLoading ? "Loading..." : "Refresh Available ZIPs"}
                </Button>

                <Divider sx={{ mt: 2 }} />

                <Typography variant="subtitle2" color="text.secondary">
                  Failed invoices
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Upload invoices that failed automatic download. Files go under{" "}
                  <code>invoices/year/month/</code>; manual uploads are marked
                  in eval as <code>submitted_subsequently</code>. Open the
                  fold-out to see all invoices for the selected month.
                </Typography>

                <input
                  ref={failedInvoiceFileInputRef}
                  type="file"
                  accept=".pdf,.csv,.xlsx,.xls,image/*"
                  multiple
                  hidden
                  onChange={handleFailedInvoiceFilesSelected}
                />
                <input
                  ref={failedInvoiceFolderInputRef}
                  type="file"
                  accept=".pdf,.csv,.xlsx,.xls,image/*"
                  multiple
                  webkitdirectory=""
                  directory=""
                  hidden
                  onChange={handleFailedInvoiceFilesSelected}
                />

                <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={
                      uploadingFailedInvoices ? (
                        <CircularProgress size={14} />
                      ) : (
                        <UploadIcon />
                      )
                    }
                    disabled={
                      !selectedZipYear ||
                      !selectedZipMonth ||
                      uploadingFailedInvoices
                    }
                    onClick={() => failedInvoiceFileInputRef.current?.click()}
                  >
                    Upload file(s)
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={
                      uploadingFailedInvoices ? (
                        <CircularProgress size={14} />
                      ) : (
                        <UploadIcon />
                      )
                    }
                    disabled={
                      !selectedZipYear ||
                      !selectedZipMonth ||
                      uploadingFailedInvoices
                    }
                    onClick={() => failedInvoiceFolderInputRef.current?.click()}
                  >
                    Upload folder
                  </Button>
                </Box>

                {failedInvoiceLoading && !failedInvoiceStatus ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 1 }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                ) : null}

                {selectedZipYear &&
                selectedZipMonth &&
                !failedInvoiceLoading ? (
                  <Accordion
                    disableGutters
                    elevation={0}
                    sx={{
                      mt: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="body2">
                        All invoices ({failedInvoiceStatus?.file_count ?? 0})
                        {failedInvoiceStatus?.submitted_subsequently_count
                          ? ` · ${failedInvoiceStatus.submitted_subsequently_count} subsequently submitted`
                          : ""}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, px: 1 }}>
                      {failedInvoiceStatus?.files?.length > 0 ? (
                        <List dense sx={{ py: 0 }}>
                          {failedInvoiceStatus.files.map((fileItem) => (
                            <ListItem key={fileItem.name}>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                {fileItem.found ? (
                                  <CheckCircleIcon
                                    color="success"
                                    fontSize="small"
                                  />
                                ) : (
                                  <ErrorIcon color="warning" fontSize="small" />
                                )}
                              </ListItemIcon>
                              <ListItemText
                                primary={fileItem.name}
                                secondary={
                                  !fileItem.found
                                    ? "Listed in eval but missing on disk"
                                    : [
                                        fileItem.size_bytes != null
                                          ? formatFileSize(fileItem.size_bytes)
                                          : null,
                                        fileItem.submitted_subsequently
                                          ? "subsequently submitted"
                                          : null,
                                      ]
                                        .filter(Boolean)
                                        .join(" · ") || "On disk"
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No invoices in this month folder yet.
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ) : null}

                <Divider sx={{ mt: 2 }} />

                <Typography variant="subtitle2" color="text.secondary">
                  Manual platform invoices
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  These invoices cannot be fetched automatically. Files are
                  stored under{" "}
                  <code>invoices/&lt;platform&gt;/year/month/</code>. Optional
                  platforms (yellow) do not block accounting email; uploaded
                  files are still included in the ZIP.
                </Typography>

                <input
                  ref={platformFileInputRef}
                  type="file"
                  accept=".pdf,.csv,.xlsx,.xls,image/*"
                  hidden
                  onChange={handleManualPlatformFileSelected}
                />

                {manualPlatformLoading && !manualPlatformStatus ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 1 }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                ) : null}

                {manualPlatformStatus?.platforms?.length > 0 && (
                  <List dense sx={{ py: 0 }}>
                    {manualPlatformStatus.platforms.map((platformItem) => (
                      <ListItem
                        key={platformItem.platform}
                        secondaryAction={
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            {platformItem.download_url && (
                              <Tooltip title="Open portal">
                                <IconButton
                                  component="a"
                                  href={platformItem.download_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  size="small"
                                >
                                  <OpenInNewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip
                              title={
                                platformItem.allow_multiple
                                  ? "Upload invoice(s)"
                                  : "Upload invoice"
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleManualPlatformUploadClick(
                                      platformItem,
                                    )
                                  }
                                  disabled={
                                    uploadingPlatform === platformItem.platform
                                  }
                                >
                                  {uploadingPlatform ===
                                  platformItem.platform ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <UploadIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        }
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {platformItem.found ? (
                            <CheckCircleIcon color="success" fontSize="small" />
                          ) : platformItem.optional ? (
                            <WarningAmberIcon
                              sx={{ color: "warning.main" }}
                              fontSize="small"
                            />
                          ) : (
                            <ErrorIcon color="error" fontSize="small" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <>
                              {platformItem.label}
                              {platformItem.optional ? (
                                <Chip
                                  size="small"
                                  label="optional"
                                  variant="outlined"
                                  color="warning"
                                  sx={{ ml: 1, height: 20 }}
                                />
                              ) : null}
                            </>
                          }
                          secondary={
                            <Box component="span" sx={{ display: "block" }}>
                              <Typography variant="caption" component="span">
                                {platformItem.found
                                  ? platformItem.files.join(", ")
                                  : platformItem.folder}
                              </Typography>
                              {platformItem.upload_hint && (
                                <Typography
                                  variant="caption"
                                  component="div"
                                  color="text.secondary"
                                  sx={{ mt: 0.5 }}
                                >
                                  {platformItem.download_url ? (
                                    <>
                                      <Link
                                        href={platformItem.download_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        Open portal
                                      </Link>
                                      {" · "}
                                    </>
                                  ) : null}
                                  {platformItem.upload_hint}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}

                {manualPlatformStatus?.missing_count > 0 && (
                  <Alert severity="warning">
                    {manualPlatformStatus.missing_count} required manual
                    platform invoice(s) still missing for{" "}
                    {String(selectedZipMonth).padStart(2, "0")}/
                    {selectedZipYear}.
                  </Alert>
                )}
                {manualPlatformStatus?.optional_missing_count > 0 && (
                  <Alert severity="info">
                    {manualPlatformStatus.optional_missing_count} optional
                    platform invoice(s) not uploaded yet (e.g. Google Ads) — ZIP
                    and accounting email can still proceed.
                  </Alert>
                )}

                <Divider sx={{ mt: 2 }} />

                <Typography variant="subtitle2" color="text.secondary">
                  Bank, credit card &amp; Otto fee statements
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Required before accounting email. Stored under{" "}
                  <code>reports/year/month/bank_creditcard/</code> and not
                  included in the email attachments ZIP. Otto fee types accept
                  multiple files each (stored in a subfolder per type).
                </Typography>

                <input
                  ref={bankCreditcardFileInputRef}
                  type="file"
                  accept=".pdf,.csv,.xlsx,.xls,image/*"
                  hidden
                  onChange={handleBankCreditcardFileSelected}
                />

                {bankCreditcardLoading && !bankCreditcardStatus ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 1 }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                ) : null}

                {bankCreditcardStatus?.items?.length > 0 &&
                  groupBankCreditcardItems(bankCreditcardStatus.items).map(
                    (group) => (
                      <Box
                        key={group.id}
                        sx={{ mt: group.id === "otto" ? 1.5 : 0.5 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600, textTransform: "uppercase" }}
                          >
                            {group.title}
                          </Typography>
                          <Chip
                            size="small"
                            label={`${
                              group.items.filter((item) => item.found).length
                            }/${group.items.length}`}
                            color={
                              group.items.every((item) => item.found)
                                ? "success"
                                : "default"
                            }
                            variant="outlined"
                          />
                        </Box>
                        <List dense sx={{ py: 0 }}>
                          {group.items.map((item) => (
                            <ListItem
                              key={item.key}
                              secondaryAction={
                                <Box sx={{ display: "flex", gap: 0.5 }}>
                                  {item.download_url && (
                                    <Tooltip title="Open portal">
                                      <IconButton
                                        component="a"
                                        href={item.download_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        size="small"
                                      >
                                        <OpenInNewIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  <Tooltip
                                    title={
                                      item.multi_file
                                        ? "Upload file(s)"
                                        : "Upload statement"
                                    }
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleBankCreditcardUploadClick(item)
                                        }
                                        disabled={
                                          uploadingBankCreditcardKey ===
                                          item.key
                                        }
                                      >
                                        {uploadingBankCreditcardKey ===
                                        item.key ? (
                                          <CircularProgress size={16} />
                                        ) : (
                                          <UploadIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Box>
                              }
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                {item.found ? (
                                  <CheckCircleIcon
                                    color="success"
                                    fontSize="small"
                                  />
                                ) : (
                                  <ErrorIcon color="error" fontSize="small" />
                                )}
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Box
                                    component="span"
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 0.75,
                                    }}
                                  >
                                    {item.label}
                                    {item.multi_file ? (
                                      <Chip
                                        size="small"
                                        label={`${item.file_count || 0} files`}
                                        variant="outlined"
                                      />
                                    ) : null}
                                  </Box>
                                }
                                secondary={
                                  <Box
                                    component="span"
                                    sx={{ display: "block" }}
                                  >
                                    <Typography
                                      variant="caption"
                                      component="span"
                                    >
                                      {bankCreditcardItemSummary(item)}
                                    </Typography>
                                    {item.upload_hint && (
                                      <Typography
                                        variant="caption"
                                        component="div"
                                        color="text.secondary"
                                        sx={{ mt: 0.5 }}
                                      >
                                        {item.download_url ? (
                                          <>
                                            <Link
                                              href={item.download_url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              Open portal
                                            </Link>
                                            {" · "}
                                          </>
                                        ) : null}
                                        {item.upload_hint}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    ),
                  )}

                {bankCreditcardStatus?.missing_count > 0 && (
                  <Alert severity="warning">
                    {bankCreditcardStatus.missing_count} bank/credit-card/Otto
                    fee statement(s) still missing for{" "}
                    {String(selectedZipMonth).padStart(2, "0")}/
                    {selectedZipYear}.
                  </Alert>
                )}

                <Divider sx={{ mt: 2 }} />

                <Typography variant="subtitle2" color="text.secondary">
                  Accounting (Buchhaltung)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Review the German email and attachments, then send manually in
                  Ionos Webmail. Automatic server send is optional and only
                  available after you confirm the preview. Requires all manual
                  platform invoices and bank/credit-card/Otto fee statements
                  above to be uploaded first.
                </Typography>

                {!accountingPrerequisitesReady &&
                  selectedZipYear &&
                  selectedZipMonth &&
                  !manualPlatformLoading &&
                  !bankCreditcardLoading && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      {isSuperuser
                        ? "Required files are incomplete. As admin you can still open the review dialog to test the email template and substitutions; download/send stay locked until ready."
                        : "Accounting is locked until all manual platform invoices and bank/credit-card/Otto fee statements for the selected month are uploaded."}
                    </Alert>
                  )}

                <Tooltip
                  title={
                    canOpenBuchhaltungReview
                      ? isSuperuser && !accountingPrerequisitesReady
                        ? "Admin: preview without all files"
                        : ""
                      : !manualPlatformInvoicesReady
                        ? "Upload all manual platform invoices first"
                        : !bankCreditcardReportsReady
                          ? "Upload all bank, credit card, and Otto fee statements first"
                          : ""
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={
                        buchhaltungLoading ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <EmailIcon />
                        )
                      }
                      onClick={openBuchhaltungDialog}
                      disabled={
                        !selectedZipYear ||
                        !selectedZipMonth ||
                        buchhaltungLoading ||
                        manualPlatformLoading ||
                        bankCreditcardLoading ||
                        !canOpenBuchhaltungReview
                      }
                      sx={{ mt: 1 }}
                    >
                      Review & send accounting email
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={buchhaltungDialogOpen}
        onClose={closeBuchhaltungDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Review accounting email</DialogTitle>
        <DialogContent dividers>
          {buchhaltungLoading && !buchhaltungPreview ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
            >
              {buchhaltungPreview && (
                <>
                  <Alert severity="warning">
                    Nothing is sent automatically until you finish in Ionos or
                    explicitly choose server send after verifying the preview
                    below.
                  </Alert>

                  <Alert severity="info">
                    <Typography variant="body2">
                      Recipient: {buchhaltungPreview.recipient}
                    </Typography>
                    <Typography variant="body2">
                      Subject: {buchhaltungPreview.subject}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Inventur date: {buchhaltungPreview.inventur_date}
                    </Typography>
                  </Alert>

                  <TextField
                    label="Datev Unternehmen online link"
                    value={datevLink}
                    onChange={(e) => setDatevLink(e.target.value)}
                    onBlur={refreshBuchhaltungPreview}
                    fullWidth
                    placeholder="Paste link manually"
                  />

                  <TextField
                    label="Inventur net (€)"
                    value={inventurNetEur}
                    onChange={(e) => setInventurNetEur(e.target.value)}
                    onBlur={refreshBuchhaltungPreview}
                    fullWidth
                    placeholder="e.g. 123.456,78"
                    helperText="Read from Plenty eval XLSX (eval/year/month/) when available"
                  />

                  <input
                    ref={buchhaltungFileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    hidden
                    onChange={handleBuchhaltungFileSelected}
                  />

                  <Typography variant="subtitle2">Attachments</Typography>
                  <List dense>
                    {buchhaltungPreview.attachments.map((attachment) => (
                      <ListItem
                        key={`${attachment.key}-${attachment.filename}`}
                        secondaryAction={
                          attachment.manual && !attachment.found ? (
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              {attachment.download_url && (
                                <Tooltip title="Download from portal">
                                  <IconButton
                                    component="a"
                                    href={attachment.download_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="small"
                                  >
                                    <OpenInNewIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Upload CSV">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleBuchhaltungUploadClick(attachment)
                                    }
                                    disabled={
                                      uploadingReportKey === attachment.key
                                    }
                                  >
                                    {uploadingReportKey === attachment.key ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <UploadIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          ) : null
                        }
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {attachment.found ? (
                            <CheckCircleIcon color="success" fontSize="small" />
                          ) : (
                            <ErrorIcon color="error" fontSize="small" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={attachment.label}
                          secondary={
                            <Box component="span" sx={{ display: "block" }}>
                              <Typography variant="caption" component="span">
                                {attachment.filename}
                                {attachment.found && attachment.size_bytes
                                  ? ` · ${formatFileSize(
                                      attachment.size_bytes,
                                    )}`
                                  : ""}
                              </Typography>
                              {attachment.upload_hint && (
                                <Typography
                                  variant="caption"
                                  component="div"
                                  color="text.secondary"
                                  sx={{ mt: 0.5 }}
                                >
                                  {attachment.manual &&
                                  attachment.download_url ? (
                                    <>
                                      <Link
                                        href={attachment.download_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        Open portal
                                      </Link>
                                      {" · "}
                                    </>
                                  ) : null}
                                  {attachment.upload_hint}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    Bank, credit card &amp; Otto fee statements
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Stored under{" "}
                    <code>
                      reports/{selectedZipYear}/
                      {String(selectedZipMonth).padStart(2, "0")}
                      /bank_creditcard/
                    </code>
                    . Not included in the accounting email attachments. Otto fee
                    types accept multiple files each.
                  </Typography>
                  {groupBankCreditcardItems(
                    buchhaltungPreview.bank_creditcard_reports?.items || [],
                  ).map((group) => (
                    <Box key={group.id} sx={{ mt: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 600, textTransform: "uppercase" }}
                        >
                          {group.title}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${
                            group.items.filter((item) => item.found).length
                          }/${group.items.length}`}
                          color={
                            group.items.every((item) => item.found)
                              ? "success"
                              : "default"
                          }
                          variant="outlined"
                        />
                      </Box>
                      <List dense sx={{ py: 0 }}>
                        {group.items.map((item) => (
                          <ListItem
                            key={item.key}
                            secondaryAction={
                              <Box sx={{ display: "flex", gap: 0.5 }}>
                                {item.download_url && (
                                  <Tooltip title="Open portal">
                                    <IconButton
                                      component="a"
                                      href={item.download_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      size="small"
                                    >
                                      <OpenInNewIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip
                                  title={
                                    item.multi_file
                                      ? "Upload file(s)"
                                      : "Upload statement"
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleBankCreditcardUploadClick(item)
                                      }
                                      disabled={
                                        uploadingBankCreditcardKey === item.key
                                      }
                                    >
                                      {uploadingBankCreditcardKey ===
                                      item.key ? (
                                        <CircularProgress size={16} />
                                      ) : (
                                        <UploadIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            }
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              {item.found ? (
                                <CheckCircleIcon
                                  color="success"
                                  fontSize="small"
                                />
                              ) : (
                                <ErrorIcon color="warning" fontSize="small" />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box
                                  component="span"
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                  }}
                                >
                                  {item.label}
                                  {item.multi_file ? (
                                    <Chip
                                      size="small"
                                      label={`${item.file_count || 0} files`}
                                      variant="outlined"
                                    />
                                  ) : null}
                                </Box>
                              }
                              secondary={
                                <Box component="span" sx={{ display: "block" }}>
                                  <Typography
                                    variant="caption"
                                    component="span"
                                  >
                                    {bankCreditcardItemSummary(item, {
                                      missingLabel: item.folder || "Missing",
                                    })}
                                  </Typography>
                                  {item.upload_hint && (
                                    <Typography
                                      variant="caption"
                                      component="div"
                                      color="text.secondary"
                                      sx={{ mt: 0.5 }}
                                    >
                                      {item.download_url ? (
                                        <>
                                          <Link
                                            href={item.download_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            Open portal
                                          </Link>
                                          {" · "}
                                        </>
                                      ) : null}
                                      {item.upload_hint}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  ))}

                  {buchhaltungPreview.missing_manual_platforms?.length > 0 && (
                    <Alert severity="error">
                      Missing manual platform invoices:{" "}
                      {buchhaltungPreview.missing_manual_platforms
                        .map((item) => item.label)
                        .join(", ")}
                    </Alert>
                  )}

                  {buchhaltungPreview.missing_bank_creditcard_reports?.length >
                    0 && (
                    <Alert severity="error">
                      Missing bank/credit-card/Otto fee statements:{" "}
                      {buchhaltungPreview.missing_bank_creditcard_reports
                        .map((item) => item.label)
                        .join(", ")}
                    </Alert>
                  )}

                  {buchhaltungPreview.missing_attachments?.length > 0 && (
                    <Alert severity="warning">
                      Missing attachments for{" "}
                      <code>
                        {buchhaltungPreview.reports_folder ||
                          `reports/${selectedZipYear}/${String(
                            selectedZipMonth,
                          ).padStart(2, "0")}`}
                      </code>
                      {buchhaltungPreview.missing_attachments.some(
                        (item) => item.key === "inventur_eval",
                      ) && (
                        <>
                          {" "}
                          / Inventur Eval:{" "}
                          <code>
                            {buchhaltungPreview.eval_folder ||
                              `eval/${selectedZipYear}/${String(
                                selectedZipMonth,
                              ).padStart(2, "0")}/`}
                          </code>
                        </>
                      )}
                      :{" "}
                      {buchhaltungPreview.missing_attachments
                        .map((item) => item.label)
                        .join(", ")}
                    </Alert>
                  )}

                  {!buchhaltungSendReady && isSuperuser && (
                    <Alert severity="info">
                      Admin preview: substitutions work with the current month /
                      Datev / inventur values. Download attachments and send
                      stay disabled until all required files are present.
                    </Alert>
                  )}

                  <TextField
                    label="Email template (German)"
                    value={emailBodyTemplate}
                    onChange={(e) => {
                      setEmailBodyTemplate(e.target.value);
                      setEmailBodyDirty(true);
                      emailBodyDirtyRef.current = true;
                    }}
                    onBlur={() => {
                      if (
                        emailBodyDirtyRef.current &&
                        emailBodyTemplate.trim()
                      ) {
                        saveBuchhaltungEmailBody();
                      }
                    }}
                    inputRef={emailBodyInputRef}
                    fullWidth
                    multiline
                    minRows={10}
                    helperText={
                      emailBodyDirty
                        ? "Unsaved template — saved on blur or via Save template. Keep {{datev_link}}, {{inventur_date}}, {{inventur_net_eur}} for live values."
                        : buchhaltungPreview.body_is_custom
                          ? "Custom template saved for this month (shared across users)."
                          : "Default template with placeholders — edit to keep changes across sessions."
                    }
                  />

                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {EMAIL_BODY_TOKENS.map(({ token, label }) => (
                      <Button
                        key={token}
                        size="small"
                        variant="outlined"
                        onClick={() => insertEmailBodyToken(token)}
                        disabled={buchhaltungLoading || emailBodySaving}
                      >
                        Insert {label}
                      </Button>
                    ))}
                    <Button
                      variant="outlined"
                      onClick={saveBuchhaltungEmailBody}
                      disabled={
                        buchhaltungLoading ||
                        buchhaltungSending ||
                        emailBodySaving ||
                        !emailBodyDirty ||
                        !emailBodyTemplate.trim()
                      }
                    >
                      {emailBodySaving ? "Saving…" : "Save template"}
                    </Button>
                    <Button
                      onClick={resetBuchhaltungEmailBody}
                      disabled={
                        buchhaltungLoading ||
                        buchhaltungSending ||
                        emailBodySaving ||
                        (!buchhaltungPreview.body_is_custom && !emailBodyDirty)
                      }
                    >
                      Reset to template
                    </Button>
                  </Box>

                  <TextField
                    label="Rendered preview"
                    value={renderedEmailBody}
                    fullWidth
                    multiline
                    minRows={10}
                    InputProps={{ readOnly: true }}
                    helperText="Live substitution of placeholders from the selected month, Datev link, and inventur net."
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={buchhaltungHumanVerified}
                        onChange={(e) =>
                          setBuchhaltungHumanVerified(e.target.checked)
                        }
                        disabled={!buchhaltungSendReady}
                      />
                    }
                    label="I have reviewed the recipient, subject, message, and all attachments listed above."
                  />
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1, px: 2, py: 1.5 }}>
          <Button onClick={closeBuchhaltungDialog}>Cancel</Button>
          <Button
            onClick={refreshBuchhaltungPreview}
            disabled={buchhaltungLoading || buchhaltungSending}
          >
            Refresh preview
          </Button>
          <Button
            onClick={() => copyText("Subject", buchhaltungPreview?.subject)}
            disabled={!buchhaltungPreview?.subject}
            startIcon={<ContentCopyIcon />}
          >
            Copy subject
          </Button>
          <Button
            onClick={() => copyText("Email body", renderedEmailBody)}
            disabled={!renderedEmailBody}
            startIcon={<ContentCopyIcon />}
          >
            Copy body
          </Button>
          <Button
            variant="outlined"
            onClick={downloadBuchhaltungAttachments}
            disabled={
              buchhaltungLoading ||
              buchhaltungZipLoading ||
              !buchhaltungSendReady
            }
            startIcon={
              buchhaltungZipLoading ? (
                <CircularProgress size={16} />
              ) : (
                <DownloadIcon />
              )
            }
          >
            Download attachments
          </Button>
          <Button
            variant="contained"
            onClick={openIonosWebmail}
            disabled={
              buchhaltungLoading ||
              !buchhaltungSendReady ||
              !buchhaltungHumanVerified
            }
            startIcon={<OpenInNewIcon />}
          >
            Open Ionos Webmail
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setBuchhaltungSendConfirmOpen(true)}
            disabled={
              buchhaltungLoading ||
              buchhaltungSending ||
              !buchhaltungSendReady ||
              !buchhaltungHumanVerified
            }
          >
            Send via email server…
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={buchhaltungSendConfirmOpen}
        onClose={() => setBuchhaltungSendConfirmOpen(false)}
      >
        <DialogTitle>Confirm server send?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This sends immediately to{" "}
            <strong>{buchhaltungPreview?.recipient}</strong> using the
            configured n8n / SMTP workflow.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Subject: {buchhaltungPreview?.subject}
          </Typography>
          <Typography variant="body2">
            Attachments:{" "}
            {buchhaltungPreview?.attachments?.filter((item) => item.found)
              .length || 0}{" "}
            file(s)
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            Prefer manual control? Use <strong>Open Ionos Webmail</strong>{" "}
            instead and send the message yourself after attaching the downloaded
            ZIP.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBuchhaltungSendConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={sendBuchhaltung}
            disabled={buchhaltungSending}
            startIcon={
              buchhaltungSending ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <EmailIcon />
              )
            }
          >
            {buchhaltungSending ? "Sending..." : "Yes, send now"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Download Control */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Download Control
          </Typography>

          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={
                downloadStatus === "running" ? <StopIcon /> : <PlayArrowIcon />
              }
              onClick={
                downloadStatus === "running" ? stopDownload : startDownload
              }
              disabled={!systemStatus.isReady && downloadStatus !== "running"}
              sx={{ minWidth: 200 }}
            >
              {downloadStatus === "running"
                ? "Stop Download"
                : "Start Download"}
            </Button>

            <Chip
              label={
                downloadProgress >= 100 && downloadStatus === "idle"
                  ? "Completed"
                  : downloadStatus.charAt(0).toUpperCase() +
                    downloadStatus.slice(1)
              }
              color={
                downloadProgress >= 100 && downloadStatus === "idle"
                  ? "success"
                  : downloadStatus === "completed"
                    ? "success"
                    : downloadStatus === "error"
                      ? "error"
                      : downloadStatus === "running"
                        ? "primary"
                        : "default"
              }
              variant="outlined"
            />

            <Chip
              label={`${new Date(
                selectedYear,
                selectedMonth - 1,
              ).toLocaleString("default", { month: "long" })} ${selectedYear}`}
              color="info"
              variant="outlined"
              sx={{
                backgroundColor: "info.light",
                color: "info.contrastText",
              }}
            />
          </Box>

          {/* Progress Bar */}
          {(downloadStatus === "running" || downloadProgress > 0) && (
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Progress
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Math.round(downloadProgress)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={downloadProgress}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          )}

          {/* Status Message */}
          {downloadStatusMessage && (
            <Alert
              severity={
                downloadStatus === "error"
                  ? "error"
                  : downloadStatus === "completed"
                    ? "success"
                    : "info"
              }
              sx={{ mt: 2 }}
            >
              <Typography variant="body2">{downloadStatusMessage}</Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Download Statistics */}
      {(downloadStatus === "running" || downloadStats.totalEmails > 0) && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Download Statistics
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: "center" }}>
                  <Typography variant="h4" color="primary.main">
                    {downloadStats.totalEmails}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total Emails
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: "center" }}>
                  <Typography variant="h4" color="info.main">
                    {downloadStats.processedEmails}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Processed
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: "center" }}>
                  <Typography variant="h4" color="success.main">
                    {downloadStats.successfulDownloads}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Successful
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: "center" }}>
                  <Typography variant="h4" color="error.main">
                    {downloadStats.failedDownloads}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Failed
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: "center" }}>
                  <Typography variant="h4" color="warning.main">
                    {downloadStats.alreadyDownloaded}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Already Downloaded
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {downloadStats.currentStep && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Current Step: <strong>{downloadStats.currentStep}</strong>
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default DownloadSubTab;
